import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useQuery, UseQueryOptions, useQueryClient } from 'react-query'
import { sendToApi } from 'api/client'
import { ChatMessage } from 'api/clients/ChatClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { chatKeys } from './chatKeys'
import { ThreadMessagesData } from './useInfiniteMessages'
import produce from 'immer'

type Data = {
  message: ChatMessage
}

interface Props {
  threadId: number
  id: number
}

export const fetchMessage = async (workspaceId: number, threadId: number, id: number) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/threads/${threadId}/messages/${id}`)

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error()
  }
  return res.data
}

export const useMessage = ({ threadId, id }: Props, options?: UseQueryOptions<Data>) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useQuery<Data>(chatKeys.message(workspaceId, threadId, id), () => fetchMessage(workspaceId, threadId, id), {
    placeholderData: () => {
      const cacheData = queryClient.getQueryData<ThreadMessagesData>(
        chatKeys.messages(workspaceId, threadId, { items: 5 }),
      )
      const existingMessage = cacheData?.pages
        .filter((page) => page.type === 'success')
        .flatMap((page) => page.data.messages)
        .find((message) => message.id === id)
      if (!existingMessage) return

      return {
        message: existingMessage,
      }
    },
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      queryClient.setQueryData<ThreadMessagesData | undefined>(
        chatKeys.messages(workspaceId, threadId, { items: 5 }),
        (oldData) => {
          if (!oldData) return oldData

          return produce(oldData, (draft) => {
            draft.pages
              .filter((page) => page.type === 'success')
              .forEach((page) => {
                page.data.messages.forEach((message: ChatMessage, index: number) => {
                  if (message.id === id) {
                    page.data.messages[index] = { ...message, ...data.message }
                  }
                })
              })
          })
        },
      )
    },
    ...options,
  })
}
